import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { 
  Map, Compass, Sword, Shield, Heart, Scroll, AlertCircle, 
  Save, RotateCcw, CheckCircle, Book 
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Toast } from '../../components/ui/toast';
import { SaveStateManager } from '../../utils/saveState/saveStateManager';
import SkillCheckSystemComponent from '../../components/SkillCheckSystemComponent';
import InventoryManagerComponent from '../../components/InventoryManagerComponent';

import { scenes } from './scenes';
import { items } from './items';
import { encounters } from './encounters';
import './DarkForestGame.css';

const client = generateClient();

const DarkForestGame = () => {
  const { gameId, characterId } = useParams();
  const navigate = useNavigate();
  
  // Game state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [gameLog, setGameLog] = useState([]);
  const [currentScene, setCurrentScene] = useState(null);
  const [activeEncounter, setActiveEncounter] = useState(null);
  const [showInventory, setShowInventory] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showSkillCheck, setShowSkillCheck] = useState(false);
  const [currentSkillCheck, setCurrentSkillCheck] = useState(null);
  
  // Refs
  const saveManagerRef = useRef(null);
  const unmountingRef = useRef(false);
  const logScrollRef = useRef(null);
  
  // Initialize save manager and load game state
  useEffect(() => {
    const initGame = async () => {
      try {
        setLoading(true);
        
        // Create save manager instance
        saveManagerRef.current = new SaveStateManager(gameId, characterId);
        
        // Load initial state or create a new one
        let savedState;
        try {
          savedState = await saveManagerRef.current.loadGameState();
        } catch (err) {
          console.log('No saved state found, creating new game state');
          savedState = createInitialGameState();
        }
        
        setGameState(savedState);
        
        // Set initial scene
        const sceneId = savedState.metadata.sceneName || 'intro';
        setCurrentScene(scenes[sceneId]);
        
        // Load game log
        if (savedState.storyState?.gameLog) {
          setGameLog(savedState.storyState.gameLog);
        }
        
        // Start auto-save
        saveManagerRef.current.startAutoSave(() => {
          handleSave(true);
        });
        
      } catch (err) {
        console.error('Error initializing game:', err);
        setError('Failed to load game state');
      } finally {
        setLoading(false);
      }
    };

    initGame();

    // Cleanup on unmount
    return () => {
      unmountingRef.current = true;
      if (saveManagerRef.current) {
        saveManagerRef.current.dispose();
      }
    };
  }, [gameId, characterId]);

  // Scroll to bottom of game log when it updates
  useEffect(() => {
    if (logScrollRef.current) {
      logScrollRef.current.scrollTop = logScrollRef.current.scrollHeight;
    }
  }, [gameLog]);
  
  // Create initial game state
  const createInitialGameState = () => {
    // Basic structure for a new game
    return {
      metadata: {
        gameId,
        characterId,
        lastSaved: new Date().toISOString(),
        sceneName: 'intro',
        turnCount: 0
      },
      characterState: {
        // This would be populated with the character data from the database
        // For now, we'll use some placeholder data
        id: characterId,
        currentHealth: 20,
        maxHealth: 20,
        experience: 0,
        level: 1,
        gold: 10,
        inventory: [
          { item: items.torch, quantity: 2, isEquipped: false },
          { item: items.rope, quantity: 1, isEquipped: false }
        ],
        skills: [],
        checkpoints: []
      },
      storyState: {
        discoveredLocations: [],
        completedObjectives: [],
        acquiredItems: [],
        storyFlags: [],
        keyDecisions: [],
        gameLog: []
      }
    };
  };
  
  // Add entry to game log
  const addToLog = (entry) => {
    const newEntry = {
      text: entry,
      timestamp: Date.now()
    };
    
    setGameLog(prevLog => [...prevLog, newEntry]);
    
    // Update game state with new log
    setGameState(prev => ({
      ...prev,
      storyState: {
        ...prev.storyState,
        gameLog: [...(prev.storyState.gameLog || []), newEntry]
      }
    }));
  };
  
  // Handle save game
  const handleSave = async (isAutoSave = false) => {
    if (!gameState || saving) return;

    try {
      setSaving(true);
      
      await saveManagerRef.current.saveGameState(gameState);
      
      setLastSaveTime(new Date());
      
      if (!isAutoSave) {
        setShowSaveToast(true);
        setTimeout(() => setShowSaveToast(false), 3000);
        addToLog("Game saved.");
      }
    } catch (err) {
      console.error('Error saving game:', err);
      setError('Failed to save game state');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle choice selection
  const handleChoice = (choice) => {
    // Check if choice requires a skill check
    if (choice.requiresSkill) {
      // Setup and display skill check
      setCurrentSkillCheck({
        skill: choice.requiresSkill,
        difficulty: choice.skillDifficulty,
        onSuccess: () => {
          processChoiceOutcome(choice, true);
          setShowSkillCheck(false);
        },
        onFailure: () => {
          processChoiceOutcome(choice, false);
          setShowSkillCheck(false);
        }
      });
      setShowSkillCheck(true);
      return;
    }
    
    // Process normal choice
    processChoiceOutcome(choice);
  };
  
  // Process choice outcome and scene transition
  const processChoiceOutcome = (choice, skillCheckSuccess = null) => {
    // Determine which scene to go to next
    let nextSceneId;
    if (skillCheckSuccess !== null) {
      nextSceneId = skillCheckSuccess ? choice.successScene : choice.failureScene;
    } else {
      nextSceneId = choice.nextScene;
    }
    
    // Update game log with the choice made
    addToLog(`You decided to ${choice.text.toLowerCase()}.`);
    
    // Check if this choice triggers an encounter
    if (choice.encounter) {
      setActiveEncounter(encounters[choice.encounter]);
      return;
    }
    
    // Update game state
    setGameState(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        sceneName: nextSceneId,
        turnCount: prev.metadata.turnCount + 1
      },
      storyState: {
        ...prev.storyState,
        keyDecisions: [...prev.storyState.keyDecisions, {
          sceneId: currentScene.id,
          decision: choice.text,
          timestamp: Date.now()
        }]
      }
    }));
    
    // Transition to the new scene
    setCurrentScene(scenes[nextSceneId]);
    
    // Auto-save after significant choices
    handleSave(true);
  };
  
  // Handle skill check results
  const handleSkillCheckResult = (result, skill) => {
    if (result.success) {
      addToLog(`Success! ${result.details.message}`);
      
      // Award experience for successful skill check
      const experienceGained = Math.floor(result.difficulty / 3) * 5;
      
      setGameState(prev => ({
        ...prev,
        characterState: {
          ...prev.characterState,
          experience: prev.characterState.experience + experienceGained
        }
      }));
      
      addToLog(`You gained ${experienceGained} experience.`);
    } else {
      addToLog(`Failure. ${result.details.message}`);
    }
  };
  
  // Handle skill experience
  const handleSkillExperience = (skillName, amount) => {
    setGameState(prev => {
      const updatedSkills = [...(prev.characterState.skills || [])];
      const skillIndex = updatedSkills.findIndex(s => 
        s.skill.name.toLowerCase() === skillName.toLowerCase()
      );
      
      if (skillIndex === -1) {
        // Character doesn't have this skill yet, add it
        updatedSkills.push({
          skill: {
            name: skillName
          },
          level: 0,
          experience: amount
        });
      } else {
        // Update existing skill
        updatedSkills[skillIndex] = {
          ...updatedSkills[skillIndex],
          experience: updatedSkills[skillIndex].experience + amount
        };
      }
      
      return {
        ...prev,
        characterState: {
          ...prev.characterState,
          skills: updatedSkills
        }
      };
    });
  };
  
  // Handle character updates (from inventory, etc.)
  const handleCharacterUpdate = (updatedCharacter) => {
    setGameState(prev => ({
      ...prev,
      characterState: {
        ...prev.characterState,
        ...updatedCharacter
      }
    }));
  };

  // Loading screen
  if (loading) {
    return (
      <Card className="dark-forest-loading">
        <CardContent className="flex justify-center items-center">
          <Scroll className="animate-spin mr-2 h-6 w-6" />
          <span>Loading the Dark Forest...</span>
        </CardContent>
      </Card>
    );
  }

  // Error screen
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
        <Button 
          className="mt-4" 
          variant="outline" 
          onClick={() => navigate('/games')}
        >
          Return to Games
        </Button>
      </Alert>
    );
  }
  
  // Main game render
  return (
    <div className="dark-forest-game">
      {/* Game Header */}
      <Card className="game-header">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>The Dark Forest</span>
            <div className="character-stats">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowStats(!showStats)}
                className="stat-button"
              >
                <Heart className="text-red-500 h-5 w-5 mr-1" />
                {gameState.characterState.currentHealth}/{gameState.characterState.maxHealth}
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowInventory(!showInventory)}
                className="stat-button"
              >
                <Scroll className="text-amber-500 h-5 w-5 mr-1" />
                {gameState.characterState.inventory?.length || 0} items
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                className="stat-button"
              >
                <Sword className="text-blue-500 h-5 w-5 mr-1" />
                XP: {gameState.characterState.experience}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
      </Card>

      {/* Main Game Content */}
      <div className="game-content">
        {/* Inventory Panel (Conditional) */}
        {showInventory && (
          <Card className="inventory-panel">
            <InventoryManagerComponent 
              character={gameState.characterState}
              onCharacterUpdate={handleCharacterUpdate}
              onItemUse={(result) => addToLog(result.message)}
            />
            <CardFooter>
              <Button 
                onClick={() => setShowInventory(false)}
                variant="outline"
                className="w-full"
              >
                Close Inventory
              </Button>
            </CardFooter>
          </Card>
        )}
        
        {/* Skill Check Panel (Conditional) */}
        {showSkillCheck && currentSkillCheck && (
          <Card className="skill-check-panel">
            <SkillCheckSystemComponent 
              character={gameState.characterState}
              requiredSkill={currentSkillCheck.skill}
              difficultyClass={currentSkillCheck.difficulty}
              onSuccess={(result) => {
                handleSkillCheckResult(result, currentSkillCheck.skill);
                currentSkillCheck.onSuccess(result);
              }}
              onFailure={(result) => {
                handleSkillCheckResult(result, currentSkillCheck.skill);
                currentSkillCheck.onFailure(result);
              }}
              onSkillExperience={handleSkillExperience}
              checkDescription={`${currentSkillCheck.skill.toUpperCase()} Check`}
            />
          </Card>
        )}

        {/* Scene Display */}
        {!showInventory && !showSkillCheck && currentScene && (
          <Card className="scene-display">
            <CardHeader>
              <CardTitle>{currentScene.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="scene-description">
                <p>{currentScene.description}</p>
              </div>
              
              <div className="choice-container">
                {currentScene.choices && currentScene.choices.map((choice, index) => (
                  <Button
                    key={index}
                    onClick={() => handleChoice(choice)}
                    className="choice-button"
                    variant="outline"
                    disabled={choice.requires && !gameState.storyState.storyFlags.includes(choice.requires)}
                  >
                    {choice.text}
                  </Button>
                ))}
              </div>
            </CardContent>
            <CardFooter className="scene-actions">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleSave()}
                disabled={saving}
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Saving...' : 'Save Game'}
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowInventory(true)}
              >
                <Scroll className="mr-2 h-4 w-4" />
                Inventory
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Game Log */}
        <Card className="game-log">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Book className="h-4 w-4" />
              Adventure Log
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-48" ref={logScrollRef}>
              {gameLog.map((entry, index) => (
                <p key={index} className="text-sm mb-2">
                  {entry.text}
                </p>
              ))}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Save Toast */}
      {showSaveToast && (
        <Toast>
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span>Game saved successfully</span>
          </div>
        </Toast>
      )}
    </div>
  );
};

export default DarkForestGame;